import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { plainAmount } from 'src/app/shared/utils/currency/currency-format';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { NgStyle } from '@angular/common';
import { SplicedBackgroundComponent } from '../../../../ui/surface/spliced-background/spliced-background.component';
import { WagerPrizeComponent } from '../wager-prize/wager-prize.component';
import { LinkButtonComponent } from '../../../../ui/buttons/link-button/link-button.component';

/** `tile`: a grid cell with a filled Play button; the whole card is the button. `row`: a
 *  full-width row, labels left and a "Play now →" text button bottom-right
 *  (wagerDrawer.practiceRow); that text button is the control, and a tap anywhere else on
 *  the card starts practice too. */
export type PracticeCardLayout = 'tile' | 'row';

@Component({
  selector: 'app-practice-card',
  templateUrl: './practice-card.component.html',
  styleUrls: ['./practice-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    ThemePipe,
    SplicedBackgroundComponent,
    WagerPrizeComponent,
    LinkButtonComponent,
  ],
})
export class PracticeCardComponent {
  readonly title = input.required<string>();
  readonly prize = input.required<string>();
  readonly buttonLabel = input.required<string>();
  readonly layout = input<PracticeCardLayout>('tile');
  // In the row the link button is the only control: the card drops its button role, tab stop
  // and name so assistive tech doesn't get a button nested in a button.
  readonly isRow = computed(() => this.layout() === 'row');

  readonly clickEvent = output<void>();

  private themeService = inject(ThemeService);
  private translocoService = inject(TranslocoService);
  protected readonly splicedBg = this.themeService.splicedBackgroundPlacement('practice-card');
  protected readonly splicedPlayBg =
    this.themeService.splicedBackgroundPlacement('wager-play-button');

  // A practice prize is a plain word ("Fun"), not a currency amount — no icon.
  readonly prizeAmount = computed(() => plainAmount(this.prize()));

  readonly cardStyle: [string, string][] = [
    ['background', 'card-wager'],
    ['border', 'card-wager'],
    ['drop-shadow', 'card-wager'],
    ['border-radius', 'card-wager'],
  ];

  readonly titleStyles: [string, string][] = [
    ['type', 'card-wager-label-for-fun'],
    ['text-color', 'card-wager-label-for-fun'],
  ];

  readonly prizeShadowStyles: [string, string][] = [
    ['type', 'card-wager-label-prize'],
    ['text-shadow', 'card-wager-label-prize'],
  ];

  readonly prizeStyles: [string, string][] = [
    ['type', 'card-wager-label-prize'],
    ['text-outer-border', 'card-wager-label-prize'],
    ['text-color', 'card-wager-label-prize'],
  ];

  readonly enterStyle: [string, string][] = [
    ['type', 'card-wager-label-free-to-play'],
    ['text-color', 'card-wager-label-free-to-play'],
  ];

  readonly playButtonStyles: [string, string][] = [
    ['background-gradient', 'button-wager-primary'],
    ['border-radius', 'button-wager-primary'],
    ['height', 'button-wager-primary'],
    ['border', 'button-wager-primary'],
    ['drop-and-inner-shadow', 'button-wager-primary'],
  ];

  readonly playButtonLabelStyles: [string, string][] = [
    ['type', 'button-wager-primary-label'],
    ['text-color', 'button-wager-primary-label'],
  ];

  readonly topPadding = 'var(--card-wager-padding-top)';
  readonly bottomPadding = 'var(--card-wager-padding-bottom)';
  readonly leftPadding = 'var(--card-wager-padding-left)';
  readonly rightPadding = 'var(--card-wager-padding-right)';
  readonly playLabel = this.translocoService.translate('components.wager-options.play');
  readonly playNowLabel = this.translocoService.translate('components.wager-options.play-now');

  onClick(): void {
    this.clickEvent.emit();
  }
}
